/**
 * Tenant → Invoice Template
 *
 * Per-firm customisation of the invoice template — every knob mirrors the
 * platform-level page but is scoped to this firm. When a firm hasn't saved
 * any override, the platform default applies. Saving switches the firm
 * to "overriding" mode; *Reset to platform default* removes the override
 * and falls back to the platform-wide template again.
 *
 * Books segregation stays intact — only the *visual template* is per-firm.
 * Tenant accounting books never appear on platform invoices and vice versa.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, FileText, Eye, Mail, Palette, RotateCcw, Lock } from "lucide-react";
import { Field, Input, Select, GoldButton, GhostButton, PageHeader } from "@/components/Ui";

const TEMPLATE_STYLES = [
    { value: "modern",  label: "Modern (gradient bar)" },
    { value: "classic", label: "Classic (double rule)" },
    { value: "minimal", label: "Minimal" },
    { value: "cabinet", label: "Cabinet (dark header)" },
];

const FONTS = [
    { value: "serif", label: "Serif (Cormorant/Playfair)" },
    { value: "sans",  label: "Sans (Inter)" },
    { value: "mono",  label: "Mono (JetBrains Mono)" },
];

const COLUMNS = [
    { key: "hsn",      label: "HSN code" },
    { key: "qty",      label: "Quantity" },
    { key: "rate",     label: "Rate" },
    { key: "discount", label: "Discount" },
    { key: "gst_pct",  label: "GST %" },
    { key: "amount",   label: "Amount" },
];

export default function TenantInvoiceTemplate() {
    const [cfg, setCfg] = useState(null);
    const [overridden, setOverridden] = useState(false);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/tenant/invoice-template");
            const { is_overridden, ...rest } = data;
            setCfg(rest);
            setOverridden(!!is_overridden);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/tenant/invoice-template", cfg);
            toast.success("Your firm's invoice template saved", {
                description: "Every invoice your firm sends from now on (firm-side download, portal-side download, email body, PDF attachment) uses this template.",
            });
            setOverridden(true);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const resetToPlatform = async () => {
        if (!window.confirm("Discard your firm's customisations and revert to the platform default template?")) return;
        setResetting(true);
        try {
            await api.delete("/tenant/invoice-template");
            toast.success("Reverted to platform default", {
                description: "Your firm now uses the Law OS standard template for new invoices.",
            });
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setResetting(false); }
    };

    const update = (k, v) => setCfg(c => ({ ...c, [k]: v }));
    const updateCol = (k, v) => setCfg(c => ({ ...c, columns: { ...c.columns, [k]: v } }));

    const [previewHtml, setPreviewHtml] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");

    const fetchPreview = useCallback(async (config) => {
        if (!config) return;
        setPreviewLoading(true);
        setPreviewError("");
        try {
            const { data } = await api.post(
                "/tenant/invoice-template/preview",
                config,
                { responseType: "text", transformResponse: [(d) => d] },
            );
            setPreviewHtml(typeof data === "string" ? data : "");
        } catch (e) {
            setPreviewError(apiError(e));
        } finally {
            setPreviewLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!cfg) return;
        const t = setTimeout(() => fetchPreview(cfg), 600);
        return () => clearTimeout(t);
    }, [cfg, fetchPreview]);

    if (!cfg) return <div className="p-6 text-sm text-muted-foreground">Loading template…</div>;

    return (
        <>
            <PageHeader
                title="Invoice Template"
                subtitle="Every customisable knob of your firm's tax invoice. Preview live · Email-ready HTML · Auto-attached PDF."
                action={
                    <div className="flex items-center gap-2">
                        {overridden && (
                            <GhostButton onClick={resetToPlatform} disabled={resetting} data-testid="tenant-invoice-template-reset">
                                <RotateCcw className="w-4 h-4 inline mr-2"/>
                                {resetting ? "Resetting…" : "Reset to platform default"}
                            </GhostButton>
                        )}
                        <GoldButton onClick={save} disabled={saving} data-testid="tenant-invoice-template-save">
                            <Save className="w-4 h-4 inline mr-2"/>{saving ? "Saving…" : "Save template"}
                        </GoldButton>
                    </div>
                }
            />

            <div className={`mb-4 px-3 py-2 border text-[11px] ${overridden ? "bg-amber-500/5 border-amber-500/30 text-amber-200" : "bg-emerald-500/5 border-emerald-500/30 text-emerald-200"}`}
                 data-testid="tenant-invoice-template-source">
                <Lock className="w-3 h-3 inline mr-1.5"/>
                {overridden
                    ? <>Your firm is currently <strong>overriding</strong> the platform default template. Any edits below apply only to your firm — they never affect any other tenant.</>
                    : <>Your firm is currently using the <strong>Law OS platform default template</strong>. Edit any field below and click <em>Save template</em> to switch to your own customised version.</>
                }
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-5">
                    <Section icon={Palette} title="Style & Branding">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Template style">
                                <Select value={cfg.template_style} onChange={e => update("template_style", e.target.value)} options={TEMPLATE_STYLES} data-testid="ten-tpl-style"/>
                            </Field>
                            <Field label="Font family">
                                <Select value={cfg.font_family} onChange={e => update("font_family", e.target.value)} options={FONTS} data-testid="ten-tpl-font"/>
                            </Field>
                            <Field label="Primary color">
                                <Input type="color" value={cfg.primary_color} onChange={e => update("primary_color", e.target.value)} data-testid="ten-tpl-primary"/>
                            </Field>
                            <Field label="Accent color">
                                <Input type="color" value={cfg.accent_color} onChange={e => update("accent_color", e.target.value)} data-testid="ten-tpl-accent"/>
                            </Field>
                            <Field label="Logo URL"><Input value={cfg.logo_url || ""} onChange={e => update("logo_url", e.target.value)} data-testid="ten-tpl-logo"/></Field>
                            <Field label="Brand name"><Input value={cfg.brand_name || ""} onChange={e => update("brand_name", e.target.value)} data-testid="ten-tpl-brand"/></Field>
                            <Field label="Tagline"><Input value={cfg.tagline || ""} onChange={e => update("tagline", e.target.value)} data-testid="ten-tpl-tagline"/></Field>
                            <Field label="Watermark (e.g. DRAFT)"><Input value={cfg.watermark || ""} onChange={e => update("watermark", e.target.value)} data-testid="ten-tpl-watermark"/></Field>
                            <Field label="Currency symbol"><Input value={cfg.currency_symbol || "₹"} onChange={e => update("currency_symbol", e.target.value)} data-testid="ten-tpl-currency"/></Field>
                            <Field label="Currency position">
                                <Select value={cfg.currency_position} onChange={e => update("currency_position", e.target.value)} options={[{value: "before", label: "Before (₹100)"}, {value: "after", label: "After (100 ₹)"}]} data-testid="ten-tpl-currency-pos"/>
                            </Field>
                        </div>
                    </Section>

                    <Section icon={FileText} title="Legal identifiers">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            <Field label="GSTIN"><Input value={cfg.gstin || ""} onChange={e => update("gstin", e.target.value)} data-testid="ten-tpl-gstin"/></Field>
                            <Field label="PAN"><Input value={cfg.pan || ""} onChange={e => update("pan", e.target.value)} data-testid="ten-tpl-pan"/></Field>
                            <Field label="CIN"><Input value={cfg.cin || ""} onChange={e => update("cin", e.target.value)} data-testid="ten-tpl-cin"/></Field>
                        </div>
                        <Field label="Registered address" className="mt-3">
                            <textarea value={cfg.registered_address || ""} onChange={e => update("registered_address", e.target.value)} rows={3}
                                      className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm" data-testid="ten-tpl-address"/>
                        </Field>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <Field label="Contact email"><Input value={cfg.contact_email || ""} onChange={e => update("contact_email", e.target.value)} data-testid="ten-tpl-email"/></Field>
                            <Field label="Contact phone"><Input value={cfg.contact_phone || ""} onChange={e => update("contact_phone", e.target.value)} data-testid="ten-tpl-phone"/></Field>
                        </div>
                    </Section>

                    <Section icon={FileText} title="Signature">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Signature image URL"><Input value={cfg.signature_image_url || ""} onChange={e => update("signature_image_url", e.target.value)} data-testid="ten-tpl-sig-url"/></Field>
                            <Field label="Authorised signatory name"><Input value={cfg.signature_name || ""} onChange={e => update("signature_name", e.target.value)} data-testid="ten-tpl-sig-name"/></Field>
                            <Field label="Designation"><Input value={cfg.signature_designation || ""} onChange={e => update("signature_designation", e.target.value)} data-testid="ten-tpl-sig-desig"/></Field>
                        </div>
                    </Section>

                    <Section icon={Mail} title="Payment block & Terms (HTML)">
                        <Field label="Payment instructions (HTML)" hint="Bank, UPI, Razorpay link, etc.">
                            <textarea value={cfg.payment_block_html || ""} onChange={e => update("payment_block_html", e.target.value)} rows={4}
                                      className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs font-mono" data-testid="ten-tpl-payment"/>
                        </Field>
                        <Field label="Terms & conditions (HTML)" className="mt-3">
                            <textarea value={cfg.terms_html || ""} onChange={e => update("terms_html", e.target.value)} rows={4}
                                      className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs font-mono" data-testid="ten-tpl-terms"/>
                        </Field>
                        <Field label="Header HTML" className="mt-3">
                            <textarea value={cfg.header_html || ""} onChange={e => update("header_html", e.target.value)} rows={2}
                                      className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs font-mono" data-testid="ten-tpl-header"/>
                        </Field>
                        <Field label="Footer HTML" className="mt-3">
                            <textarea value={cfg.footer_html || ""} onChange={e => update("footer_html", e.target.value)} rows={2}
                                      className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs font-mono" data-testid="ten-tpl-footer"/>
                        </Field>
                    </Section>

                    <Section icon={FileText} title="Line-item columns">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {COLUMNS.map(c => (
                                <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`ten-tpl-col-${c.key}`}>
                                    <input type="checkbox" checked={!!cfg.columns?.[c.key]} onChange={e => updateCol(c.key, e.target.checked)}/>
                                    <span>{c.label}</span>
                                </label>
                            ))}
                        </div>
                    </Section>
                </div>

                <div className="lg:sticky lg:top-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="overline inline-flex items-center gap-2">
                            <Eye className="w-3 h-3"/>
                            Live preview {previewLoading && <span className="text-[10px] text-muted-foreground ml-1">rendering…</span>}
                        </div>
                        <GhostButton onClick={() => fetchPreview(cfg)} data-testid="ten-tpl-refresh-preview" className="text-xs">Refresh</GhostButton>
                    </div>
                    <div className="text-[10px] text-muted-foreground mb-2">
                        Rendered against a built-in sample invoice using your firm&apos;s name — edits appear within a second.
                    </div>
                    {previewError ? (
                        <div className="border border-rose-300 bg-rose-50 text-rose-900 p-4 text-xs whitespace-pre-wrap" data-testid="ten-tpl-preview-error">
                            Preview failed: {previewError}
                        </div>
                    ) : (
                        <iframe
                            srcDoc={previewHtml}
                            className="w-full h-[800px] border border-border bg-white"
                            title="Invoice preview"
                            data-testid="ten-tpl-preview-iframe"
                            sandbox=""
                        />
                    )}
                </div>
            </div>
        </>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div className="border border-border bg-card">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <Icon className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="text-sm">{title}</div>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}
